import { computed, inject, Injectable } from '@angular/core';
import { GlobalsStore } from '../../stores/globals.state';
import { CommuneStore } from '../../stores/commune/commune.state';
import { AtlasConfiguration, Commune } from '../../models/commune';

/** Keys of the nested atlasConfig sections that carry their own colorset. */
export type ColorsetDefaultPath =
  | 'map'
  | 'bar'
  | 'line'
  | 'pie'
  | 'polarArea'
  | 'radar';

export type ColorsetDefaultKey = 'general' | ColorsetDefaultPath;

export interface ColorsetDefaultSlot {
  key: ColorsetDefaultKey;
  label: string;
  icon: string;
  /** `null` = root level (`atlasConfig.colorset`) */
  path: ColorsetDefaultPath | null;
}

export const colorsetDefaultSlots: ColorsetDefaultSlot[] = [
  { key: 'general', label: 'Allgemein', icon: 'fas fa-globe', path: null },
  { key: 'map', label: 'Karten', icon: 'fas fa-map', path: 'map' },
  { key: 'bar', label: 'Balken', icon: 'fas fa-chart-bar', path: 'bar' },
  { key: 'line', label: 'Linie', icon: 'fas fa-chart-line', path: 'line' },
  { key: 'pie', label: 'Kreis', icon: 'fas fa-chart-pie', path: 'pie' },
  {
    key: 'polarArea',
    label: 'Polar',
    icon: 'fas fa-snowflake',
    path: 'polarArea',
  },
  {
    key: 'radar',
    label: 'Radar',
    icon: 'fas fa-satellite-dish',
    path: 'radar',
  },
];

/**
 * Central read/write access to the commune-wide default colorsets that live
 * in `commune.atlasConfig` (general, map and one per chart type). Used by the
 * colorset administration; the configuration panels write the same fields.
 */
@Injectable({ providedIn: 'root' })
export class ColorsetDefaultsService {
  private readonly globalsStore = inject(GlobalsStore);
  private readonly communeStore = inject(CommuneStore);

  readonly slots = colorsetDefaultSlots;

  /**
   * Active commune, preferably taken from the CommuneStore: the configuration
   * panels mutate the commune in place and only the store's `items` signal
   * notifies about that change.
   */
  private readonly commune = computed(() => {
    const active = this.globalsStore.activeCommune();
    if (!active?._id) return active;
    return this.communeStore.items()[active._id] || active;
  });

  /** Current colorset id per slot (undefined = not set). */
  readonly defaults = computed(() => {
    const config = this.commune()?.atlasConfig;
    const result = {} as Record<ColorsetDefaultKey, string | undefined>;
    for (const slot of this.slots) {
      result[slot.key] = this.readSlot(config, slot) || undefined;
    }
    return result;
  });

  /**
   * Returns the slots in which the given colorset is used as default.
   */
  usageOf(colorsetId: string | undefined): ColorsetDefaultSlot[] {
    if (!colorsetId) return [];
    const defaults = this.defaults();
    return this.slots.filter(slot => defaults[slot.key] === colorsetId);
  }

  setDefault(key: ColorsetDefaultKey, colorsetId: string | null): void {
    this.applyChanges({ [key]: colorsetId });
  }

  /**
   * Removes the given colorset from every slot it is assigned to,
   * e.g. before the colorset gets deleted.
   */
  clearUsagesOf(colorsetId: string): void {
    const changes: Partial<Record<ColorsetDefaultKey, null>> = {};
    for (const slot of this.usageOf(colorsetId)) changes[slot.key] = null;
    if (Object.keys(changes).length) this.applyChanges(changes);
  }

  /**
   * Merges the slot changes immutably into the active commune's atlasConfig
   * and persists the commune. Nested sections (e.g. `bar`) keep their other
   * settings.
   */
  private applyChanges(
    changes: Partial<Record<ColorsetDefaultKey, string | null>>
  ): void {
    const commune = this.commune();
    if (!commune) return;

    let atlasConfig: AtlasConfiguration = { ...commune.atlasConfig };
    for (const slot of this.slots) {
      if (!(slot.key in changes)) continue;
      const colorset = changes[slot.key] || '';
      atlasConfig = slot.path
        ? {
            ...atlasConfig,
            [slot.path]: { ...atlasConfig[slot.path], colorset },
          }
        : { ...atlasConfig, colorset };
    }

    const updated: Commune = { ...commune, atlasConfig };
    this.globalsStore.patchActiveCommune(updated);
    this.communeStore.update(updated);
  }

  private readSlot(
    config: AtlasConfiguration | undefined,
    slot: ColorsetDefaultSlot
  ): string | undefined {
    if (!config) return undefined;
    return slot.path ? config[slot.path]?.colorset : config.colorset;
  }
}
